/**
 * EditorialItemReview.tsx — FR-017 (V2/V3/V4) / FR-018 / T317 / V3I-FR-005 / V3I-FR-012 C5.
 *
 * Bindings:
 *   - api.assetQueries.getAssetById — REAL query (convex/assetQueries.ts:21);
 *     the editorial-review surface reads the asset that the inbox sends to
 *     review.
 *   - api.annotations.updateStatus — REAL public mutation
 *     (convex/annotations.ts:266). The Accept / Reject buttons each
 *     invoke this single wrapped mutation with a different `status`
 *     argument. See `verification/errors.md` T317 §1 for why the brief's
 *     `"accepted" | "rejected"` literals are remapped to
 *     `"active" | "resolved"` (the actual members of
 *     `annotationStatusValidator`).
 *
 * Wrapper contract (V3I-FR-005, V3I-INV-12):
 *   - One `useMutationWithToast(api.annotations.updateStatus, {...})`
 *     consumed by BOTH buttons. Each click passes a different `status`
 *     arg to the wrapper's `run(...)`.
 *   - `logLabel: "editorial-review"`,
 *     `mutationName: "annotations.updateStatus"`.
 *   - NOT in `RETRY_SAFE_MUTATIONS` (single-write status transition —
 *     PRD §6.7 / `mutation-policy.ts`).
 *
 * Honest-state contract:
 *   - When the surface has no `annotationId` (the deployed route only
 *     carries an `itemId` interpreted as an `assetId`), the decision
 *     buttons render DISABLED with a tooltip `"Anotación no asignada"`.
 *     See `verification/errors.md` T317 §2.
 *   - When `annotationId` is supplied (future route wiring), the
 *     buttons are clickable and call the wired mutation.
 *
 * Glossary-clean copy via src/i18n/operator-copy.ts.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import type { Id } from '@convex/dataModel';
import {
  Breadcrumbs,
  Btn,
  EmptyState,
  Field,
  Input,
  PageHead,
  Panel,
  Skeleton,
  Thumb,
} from '../../components/primitives';
import { EDITORIAL_REVIEW_COPY } from '../../i18n/operator-copy';
// T318 — the mock fixture import retired; the review surface reads the asset
// via `api.assetQueries.getAssetById` and renders honest skeleton / empty
// states. No fixture dependency remains.
import { fmtDur, ago } from '../../lib/format';
import { useState } from 'react';
import { useMutationWithToast } from '../../hooks/useMutationWithToast';
import { navigate } from '../../lib/nav';

interface EditorialItemReviewProps {
  itemId?: string;
  /**
   * Optional id of the `annotations` row the Accept / Reject decision
   * applies to. The deployed `/editorial-inbox/:itemId` route does NOT
   * carry an annotation id (the URL param is an `assetId`), so this
   * prop is undefined in the current route binding and the decision
   * buttons render disabled. A future schedule-context embedding can
   * pass a real id; the wired mutation is identical in either case.
   */
  annotationId?: string;
}

interface AssetShape {
  _id?: string;
  title?: string;
  source?: string;
  durationMs?: number;
  addedAtMs?: number;
}

export function EditorialItemReview({
  itemId,
  annotationId,
}: EditorialItemReviewProps) {
  const [comment, setComment] = useState('');

  // Real Convex subscription on the single asset being reviewed. The query
  // is `skip`-gated when no id is supplied so the empty-state branch fires
  // without a network round-trip.
  // Guard a malformed id (hand-typed / stale URL): it would fail the
  // `v.id("assets")` validator and throw an ArgumentValidationError straight
  // to the error boundary. Convex ids are long lowercase-alphanumeric
  // strings; only query when plausibly well-formed, else fall through to the
  // not-found state below. (No backend change.)
  const validItemId = !!itemId && /^[a-z0-9]{20,}$/.test(itemId);
  const asset = useQuery(
    api.assetQueries.getAssetById,
    validItemId ? { assetId: itemId as any } : 'skip',
  ) as AssetShape | null | undefined;

  // T317 / C5 — one wrapped mutation, two consumers. The Accept button
  // runs it with `status: "active"` (operator picks up the thread —
  // matches the convenience reopen at convex/annotations.ts:251-252); the
  // Reject button runs it with `status: "resolved"` (operator closes the
  // thread — matches the `resolve` mutation at convex/annotations.ts:317).
  // The exact literal-mapping rationale is recorded in
  // verification/errors.md T317 §1.
  const { run: updateAnnotationStatus, loading: decisionLoading } =
    useMutationWithToast(api.annotations.updateStatus, {
      logLabel: 'editorial-review',
      mutationName: 'annotations.updateStatus',
    });

  // No item id, or a malformed one — surface the not-found state.
  if (!itemId || !validItemId) {
    return (
      <div>
        <PageHead title={EDITORIAL_REVIEW_COPY.pageTitle} />
        <Panel>
          <EmptyState
            icon="search"
            title={EDITORIAL_REVIEW_COPY.notFoundTitle}
            body={EDITORIAL_REVIEW_COPY.notFoundBody}
          />
        </Panel>
      </div>
    );
  }

  // Loading state.
  if (asset === undefined) {
    return (
      <div>
        <PageHead title={EDITORIAL_REVIEW_COPY.pageTitle} />
        <Skeleton h={320} />
      </div>
    );
  }

  // Honest-empty when the asset is missing / was removed.
  if (asset === null) {
    return (
      <div>
        <PageHead title={EDITORIAL_REVIEW_COPY.pageTitle} />
        <Panel>
          <EmptyState
            icon="search"
            title={EDITORIAL_REVIEW_COPY.notFoundTitle}
            body={EDITORIAL_REVIEW_COPY.notFoundBody}
          />
        </Panel>
      </div>
    );
  }

  const title = asset.title ?? 'Sin título';
  const source = asset.source ?? '—';
  const duration = fmtDur(asset.durationMs ?? 0);
  const agoLabel = asset.addedAtMs != null ? ago(asset.addedAtMs) : '—';

  const metaRows: Array<readonly [string, string]> = [
    ['Fuente', source],
    ['Duración', duration],
    ['Título original', title],
    ['Recibido', agoLabel],
  ];

  // The wired callback path. When no `annotationId` is supplied we
  // disable the buttons at the JSX level — but the function is defined
  // unconditionally so the click → callback contract is testable.
  async function decide(status: 'active' | 'resolved'): Promise<void> {
    if (!annotationId) return;
    try {
      await updateAnnotationStatus({
        annotationId: annotationId as unknown as Id<'annotations'>,
        status,
      });
      navigate('/editorial-inbox');
    } catch {
      // The wrapper has already surfaced the error toast; suppress the
      // unhandled rejection so the success-path navigation does not
      // fire on failure.
    }
  }

  const decisionDisabled = !annotationId || decisionLoading;
  const decisionTooltip = !annotationId
    ? 'Anotación no asignada'
    : undefined;

  return (
    <div>
      <Breadcrumbs
        items={[
          { label: EDITORIAL_REVIEW_COPY.breadcrumbBack, href: '#/editorial-inbox' },
          { label: title },
        ]}
      />
      <PageHead title={title} />
      <div className="circa-cols-8-4">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <Panel title="Vista previa" pad={0}>
            <div style={{ padding: 20 }}>
              <Thumb radius={14} />
            </div>
          </Panel>
          <Panel title={EDITORIAL_REVIEW_COPY.commentLabel}>
            <Field label={EDITORIAL_REVIEW_COPY.commentLabel}>
              <Input
                value={comment}
                onChange={setComment}
                placeholder={EDITORIAL_REVIEW_COPY.commentPlaceholder}
              />
            </Field>
          </Panel>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <Panel title="Información">
            {metaRows.map(([k, v]) => (
              <div
                key={k}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '8px 0',
                  borderBottom: '1px solid var(--lightest-gray)',
                  fontSize: 13,
                }}
              >
                <span style={{ color: 'var(--muted-slate)' }}>{k}</span>
                <span style={{ textAlign: 'right' }}>{v}</span>
              </div>
            ))}
          </Panel>
          <Panel title="Decisión">
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              title={decisionTooltip}
            >
              <Btn
                variant="solid"
                full
                icon="check"
                disabled={decisionDisabled}
                loading={decisionLoading}
                onClick={() => {
                  void decide('active');
                }}
              >
                {EDITORIAL_REVIEW_COPY.actionAccept}
              </Btn>
              <Btn
                variant="danger"
                full
                disabled={decisionDisabled}
                loading={decisionLoading}
                onClick={() => {
                  void decide('resolved');
                }}
              >
                {EDITORIAL_REVIEW_COPY.actionReject}
              </Btn>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

export default EditorialItemReview;
